import {
  PROCEDIMENTOS,
  procedimentosPendentes,
  procedimentosVisiveis,
  type IdProcedimento,
} from '../dominio/didatico/procedimentosInternos'
import type { PerfilVeiculo } from '../dominio/didatico/perfilVeiculo'
import type { EfeitoGeometricoAcao } from '../dominio/didatico/efeitoAcao'

export interface EfeitoUltimaAcao {
  idProcedimento: IdProcedimento
  nome: string
  /** a ação em questão foi marcada (true) ou desmarcada (false) */
  ligado: boolean
  efeito: EfeitoGeometricoAcao
  /** presente só para os três procedimentos que mexem numa peça do banco */
  silhuetaBanco: { reclinado: boolean; recuado: boolean; apoioCabecaVisivel: boolean } | null
}

interface Props {
  perfil: PerfilVeiculo
  idAncoraFoco: string
  feitos: readonly IdProcedimento[]
  /** abertura de saída em estudo: os procedimentos da traseira só aparecem na saída pela tampa */
  idAbertura?: string | null
  aoAlternar: (id: IdProcedimento) => void
  /** Fase 4: efeito da última ação alternada, isolado do acumulado */
  efeito?: EfeitoUltimaAcao | null
  mostrarPosicaoAnterior?: boolean
  aoAlternarMostrarPosicaoAnterior?: (v: boolean) => void
}

const formatarFolga = (metros: number): string =>
  Number.isFinite(metros) ? `${(metros * 100).toFixed(0)} cm` : 'sem obstáculo na rota'

/**
 * Procedimentos internos: o que a equipe faz DENTRO da cabine antes de
 * cortar — abaixar vidros, freio de mão, proteger o airbag, cobrir a vítima,
 * mexer nos bancos.
 *
 * Cada item marcado vira estado de peça de verdade (`estadosDosProcedimentos`,
 * já composto em `App.tsx`): reclinar o encosto aqui é a MESMA reclinação que
 * aparece em "O que está no caminho" — não uma segunda simulação.
 */
export function PainelProcedimentos({
  perfil,
  idAncoraFoco,
  feitos,
  idAbertura,
  aoAlternar,
  efeito,
  mostrarPosicaoAnterior = false,
  aoAlternarMostrarPosicaoAnterior,
}: Props) {
  const pendentes = procedimentosPendentes(perfil, idAncoraFoco, feitos, idAbertura)
  // o que não se aplica a esta saída nem aparece como bloqueado
  const visiveis = procedimentosVisiveis(perfil, idAbertura)
  const idsPendentes = new Set(pendentes.map((p) => p.id))
  // um procedimento cujo pré-requisito falta permanece na lista, só desabilitado —
  // some da lista de "pendentes" mas o aluno ainda vê que existe e por que está bloqueado
  const bloqueados = visiveis.filter((id) => !feitos.includes(id) && !idsPendentes.has(id))

  return (
    <div className="grupo-controle">
      <span className="titulo-controle">Procedimentos internos</span>
      <p className="dica">
        O que a equipe faz dentro da cabine antes de qualquer corte — na ordem sugerida, mas nada
        impede fazer fora dela quando a cena pedir.
      </p>
      <ul className="checklist-procedimentos">
        {visiveis.map((id) => {
          const procedimento = PROCEDIMENTOS[id]
          const feito = feitos.includes(id)
          const bloqueado = bloqueados.includes(id)
          return (
            <li key={id} className={feito ? 'feito' : bloqueado ? 'bloqueado' : ''}>
              <label className="toggle">
                <input type="checkbox" checked={feito} disabled={bloqueado} onChange={() => aoAlternar(id)} />
                {procedimento.nome}
              </label>
              <p className="dica">{procedimento.porque}</p>
              {bloqueado && (
                <p className="dica">
                  Depende de: {procedimento.requer.map((r) => PROCEDIMENTOS[r].nome).join(', ')}
                </p>
              )}
            </li>
          )
        })}
      </ul>

      {efeito && (
        <article className="efeito-ultima-acao">
          <strong>
            Efeito de "{efeito.nome}" ({efeito.ligado ? 'executado' : 'desfeito'})
          </strong>
          {efeito.efeito.mudouGeometricamente ? (
            <>
              <span>
                Folga mínima: {formatarFolga(efeito.efeito.folgaAntes)} → {formatarFolga(efeito.efeito.folgaDepois)}
              </span>
              <span>
                Interferências: {efeito.efeito.interferenciasAntes} → {efeito.efeito.interferenciasDepois}
              </span>
            </>
          ) : (
            <span>
              Sem efeito geométrico nesta rota. Isso não quer dizer que a ação não importa:{' '}
              {PROCEDIMENTOS[efeito.idProcedimento].porque}
            </span>
          )}
          {efeito.silhuetaBanco && aoAlternarMostrarPosicaoAnterior && (
            <label className="toggle">
              <input
                type="checkbox"
                checked={mostrarPosicaoAnterior}
                onChange={(e) => aoAlternarMostrarPosicaoAnterior(e.target.checked)}
              />
              mostrar a posição anterior do banco na cena
            </label>
          )}
        </article>
      )}
    </div>
  )
}
